import React from 'react';
import { Grid, Card, CardContent, Typography, Box, Chip, Avatar, Divider, Stack } from '@mui/material';
import { formatCurrency, formatPercent } from '../../../utils/mortgageUtils';

const getTrackTypeInHebrew = (trackTypeId) => {
  const trackTypes = {
    1: 'פריים',
    2: 'קבועה צמודה',
    3: 'קבועה לא צמודה',
    4: 'משתנה כל 5 שנים',
    5: 'משתנה כל שנה',
    14: 'משתנה כל 3 שנים לא צמודה'
  };
  return trackTypes[trackTypeId] || 'מסלול';
};

const defaultColors = {
  main: '#607d8b',
  light: '#eceff1',
  dark: '#455a64',
  border: '#cfd8dc'
};

const getTrackColor = (trackTypeId) => {
  const colors = {
    1: {
      main: '#2196f3',
      light: '#e3f2fd',
      dark: '#1565c0',
      border: '#bbdefb'
    },
    2: {
      main: '#4caf50',
      light: '#e8f5e9',
      dark: '#2e7d32',
      border: '#c8e6c9'
    },
    3: {
      main: '#9c27b0',
      light: '#f3e5f5',
      dark: '#7b1fa2',
      border: '#e1bee7'
    },
    4: {
      main: '#ff9800',
      light: '#fff3e0',
      dark: '#ef6c00',
      border: '#ffe0b2'
    },
    5: {
      main: '#f44336',
      light: '#ffebee',
      dark: '#c62828',
      border: '#ffcdd2'
    },
    14: {
      main: '#009688',
      light: '#e0f2f1',
      dark: '#00695c',
      border: '#b2dfdb'
    }
  };
  return colors[trackTypeId] || defaultColors;
};

const MortgageTracksList = ({ tracks = [] }) => {
  return (
    <Grid container spacing={3}>
      {tracks.map((track, index) => {
        const trackTypeId = parseInt(track.type);
        const hebrewType = getTrackTypeInHebrew(trackTypeId);
        const trackColor = getTrackColor(trackTypeId);
        
        return (
          <Grid item xs={12} md={6} key={index}>
            <Card 
              elevation={2} 
              sx={{ 
                height: '100%',
                border: `1px solid ${trackColor.border}`,
                borderTop: `4px solid ${trackColor.main}`,
                borderRadius: '8px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
                }
              }}
            >
              <CardContent>
                <Box 
                  sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    mb: 2
                  }}
                >
                  <Box>
                    <Chip 
                      avatar={<Avatar sx={{ bgcolor: trackColor.main, color: 'white' }}>{index + 1}</Avatar>}
                      label={hebrewType} 
                      sx={{ 
                        mb: 1,
                        bgcolor: trackColor.light,
                        color: trackColor.dark,
                        fontWeight: 'bold'
                      }}
                    />
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 0.5 }}>
                      {formatCurrency(track.amount)}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography 
                      variant="h5" 
                      sx={{ 
                        fontWeight: 'bold',
                        color: trackColor.main
                      }}
                    >
                      {formatPercent(track.baseInterest + track.margin)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      ריבית כוללת
                    </Typography>
                  </Box>
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      תקופה
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {track.period} שנים
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      תשלום חודשי
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {formatCurrency(track.monthlyPayment)}
                    </Typography>
                  </Grid>
                </Grid>
                
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    פירוט ריבית
                  </Typography>
                  <Stack 
                    direction="row" 
                    spacing={1} 
                    sx={{ mt: 0.5 }}
                    alignItems="center"
                  >
                    <Chip 
                      size="small" 
                      label={`בסיס: ${formatPercent(track.baseInterest)}`}
                      sx={{ bgcolor: trackColor.light, color: trackColor.dark }}
                    />
                    <Typography variant="body2">+</Typography>
                    <Chip 
                      size="small" 
                      label={`מרווח: ${formatPercent(track.margin)}`}
                      sx={{ bgcolor: '#f5f5f5' }}
                    />
                  </Stack>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default MortgageTracksList; 